import { environment } from '../config/environment';
import {
  getFromBackendAPI,
  deleteFromBackendAPI,
  uploadFileToBackendAPI,
  downloadFileFromBackendAPI,
  patchToBackendAPI,
} from './api.service';

// DTOs matching backend ClientDocumentsController

export interface DocumentTag {
  key: string;
  value: string;
}

export interface ClientDocumentFileDto {
  id: number;
  clientDocumentId: string;
  version: number;
  fileName: string;
  fileSize: number;
  contentType: string;
  uploadedByEmail?: string;
  uploadedByName?: string;
  uploadedAt: string;
}

export interface ClientDocumentDto {
  id: string;
  clientId: string;
  tags: DocumentTag[];
  createdAt: string;
  latestVersion?: ClientDocumentFileDto;
  /**
   * Set when the document was created as a placeholder (e.g. "Bank Statement").
   * Used as the display name in lists/dialogs while latestVersion is undefined.
   */
  placeholderName?: string;
}

/**
 * Get all documents for a client with latest version metadata
 */
export async function getClientDocuments(clientId: string): Promise<ClientDocumentDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.clientDocuments}/client/${clientId}`;
  return await getFromBackendAPI<ClientDocumentDto[]>(endpoint);
}

/**
 * Create a new client document with an initial file upload
 */
export async function createClientDocument(
  clientId: string,
  file: File,
  tags: DocumentTag[] = []
): Promise<ClientDocumentDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.clientDocuments}`;
  const formData = new FormData();
  formData.append('clientId', clientId);
  formData.append('file', file);
  formData.append('tagsJson', JSON.stringify(tags));
  return await uploadFileToBackendAPI<ClientDocumentDto>(endpoint, formData);
}

/**
 * Update tags on a client document
 */
export async function updateClientDocumentTags(
  documentId: string,
  tags: DocumentTag[]
): Promise<ClientDocumentDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.clientDocuments}/${documentId}/tags`;
  return await patchToBackendAPI<ClientDocumentDto>(endpoint, { tags });
}

/**
 * Delete a client document and all its versions
 */
export async function deleteClientDocument(documentId: string): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.clientDocuments}/${documentId}`;
  return await deleteFromBackendAPI<void>(endpoint);
}

/**
 * Get all versions of a client document
 */
export async function getClientDocumentVersions(documentId: string): Promise<ClientDocumentFileDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.clientDocuments}/${documentId}/versions`;
  return await getFromBackendAPI<ClientDocumentFileDto[]>(endpoint);
}

/**
 * Upload a new version of a client document
 */
export async function uploadClientDocumentVersion(documentId: string, file: File): Promise<ClientDocumentFileDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.clientDocuments}/${documentId}/versions`;
  const formData = new FormData();
  formData.append('file', file);
  return await uploadFileToBackendAPI<ClientDocumentFileDto>(endpoint, formData);
}

/**
 * Download a specific version of a client document
 */
export async function downloadClientDocumentVersion(
  documentId: string,
  version: number,
  fileName?: string
): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.clientDocuments}/${documentId}/versions/${version}/download`;
  const blob = await downloadFileFromBackendAPI(endpoint);

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || `document_v${version}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Delete a specific version of a client document
 */
export async function deleteClientDocumentVersion(documentId: string, version: number): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.clientDocuments}/${documentId}/versions/${version}`;
  return await deleteFromBackendAPI<void>(endpoint);
}
